import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { LEVEL_LABELS } from "@/data/onboarding-topics";
import type { HebrewLevel } from "@/types";
import { cn } from "@/lib/utils";

interface LevelStepProps {
  selectedLevel: HebrewLevel | undefined;
  onSelectLevel: (level: HebrewLevel) => void;
  onContinue: () => void;
  onBack: () => void;
}

const LEVELS: HebrewLevel[] = ["beginner", "basics", "conversational", "advanced"];

export function LevelStep({
  selectedLevel,
  onSelectLevel,
  onContinue,
  onBack,
}: LevelStepProps) {
  return (
    <div className="flex flex-col items-center text-center gap-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          How much Hebrew do you know?
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          We'll match your starter vocabulary to your level
        </p>
      </div>

      <div className="w-full max-w-sm grid grid-cols-2 gap-3">
        {LEVELS.map((level) => {
          const info = LEVEL_LABELS[level];
          return (
            <motion.button
              key={level}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelectLevel(level)}
              className={cn(
                "flex flex-col items-center gap-1 px-4 py-4 rounded-xl border-2 text-sm transition-colors",
                selectedLevel === level
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-950 dark:border-blue-400"
                  : "border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
              )}
            >
              <span className="text-2xl">{info.emoji}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {info.label}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {info.description}
              </span>
            </motion.button>
          );
        })}
      </div>

      <div className="flex gap-3 w-full max-w-sm">
        <Button variant="outline" onClick={onBack} size="lg">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button
          onClick={onContinue}
          className="flex-1"
          size="lg"
          disabled={!selectedLevel}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
